import React, { useState } from 'react';
import { isDebugMode } from '@sluitstraat/core';
import { debugPanelStyles as styles } from '../styles/commonStyles';

interface DebugPanelProps {
  input: any;
  output: any;
  onRecalculate?: () => void;
  calculating?: boolean;
  /** Extra disable for the calc button beyond `calculating` (e.g. monument/rented = not insurable). */
  calcDisabled?: boolean;
  /** Override the calc button label when not calculating (e.g. 'Monument - niet verzekerbaar'). */
  calcLabel?: string;
  /** Calculation error to surface inside the panel. */
  error?: string | null;
  /** Validation errors currently blocking "Volgende" (field path -> message). Shown so a
      missing/invalid value is visible even when the page just scrolls without a message. */
  validationErrors?: Record<string, string>;
}

// Canonical debug panel for every product screen (Personenauto / Woonhuis / Inboedel /
// Aansprakelijkheid / Rechtsbijstand / Doorlopende reis). Shows the BFF calc request/response,
// an optional manual recalc button, an optional calc error, and the current validation errors.
// Styles live in commonStyles (debugPanelStyles) — no CSS in this file.
export const DebugPanel: React.FC<DebugPanelProps> = ({
  input,
  output,
  onRecalculate,
  calculating = false,
  calcDisabled = false,
  calcLabel,
  error,
  validationErrors,
}) => {
  const [open, setOpen] = useState(false);
  if (!isDebugMode()) return null;

  const calcBusy = calculating || calcDisabled;
  const validationEntries = validationErrors ? Object.entries(validationErrors) : [];

  return (
    <div style={styles.panel}>
      <div style={styles.header}>
        <h3 style={styles.title}>Technical Details (Debug)</h3>
        <button type="button" style={styles.toggle} onClick={() => setOpen(!open)}>
          {open ? '▼ Hide' : '▶ Show'}
        </button>
      </div>

      {open && (
        <div style={styles.content}>
          {onRecalculate && (
            <button
              type="button"
              style={{ ...styles.calcButton, ...(calcBusy ? styles.calcButtonDisabled : {}) }}
              onClick={onRecalculate}
              disabled={calcBusy}
            >
              {calculating ? 'Berekenen...' : (calcLabel ?? 'Bereken Premie')}
            </button>
          )}

          {error && <div style={styles.errorBox}>{error}</div>}

          {validationEntries.length > 0 && (
            <div style={styles.section}>
              <h4 style={styles.sectionTitle}>Validatie blokkeert Volgende ({validationEntries.length})</h4>
              <pre style={styles.pre}>{JSON.stringify(validationErrors, null, 2)}</pre>
            </div>
          )}

          {(input || output) && (
            <>
              <div style={styles.section}>
                <h4 style={styles.sectionTitle}>Request Sent to BFF</h4>
                <pre style={styles.pre}>{JSON.stringify(input, null, 2)}</pre>
              </div>
              <div style={styles.section}>
                <h4 style={styles.sectionTitle}>Response from BFF</h4>
                <pre style={styles.pre}>{JSON.stringify(output, null, 2)}</pre>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default DebugPanel;
